import { APP_TIME_ZONE, APP_TIME_ZONE_LABEL } from "./locale";

const minuteMilliseconds = 60_000;

/**
 * The earliest boundary the control plane accepts, as a local calendar date.
 * The API enforces the same floor on the instant it receives.
 */
const EARLIEST_HISTORY_DATE = "2000-01-01";

/**
 * Wall-clock components of a UTC instant as observed in `timeZone`.
 *
 * The same two-step conversion `packages/knowledge/src/commitments.ts` uses to
 * resolve a deadline, written out again here rather than imported: that package
 * is a server package and pulling it into the browser bundle to borrow fifteen
 * lines of arithmetic would be the larger mistake. The two must agree, so they
 * are the same algorithm — offset measured at a guess, then measured again at
 * the corrected instant, which is what makes a zone with daylight saving land
 * on the right side of its own transition.
 */
function zonedParts(instant: number): {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
} {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(instant));
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
}

/**
 * Offset (local − UTC), in ms, the application zone applies at `instant`.
 * Callers pass second-aligned instants, which is what makes the subtraction
 * exact rather than off by the milliseconds the formatter does not report.
 */
function zoneOffsetMilliseconds(instant: number): number {
  const local = zonedParts(instant);
  return (
    Date.UTC(
      local.year,
      local.month - 1,
      local.day,
      local.hour,
      local.minute,
      local.second,
    ) - instant
  );
}

/** The UTC instant at which a `YYYY-MM-DD` calendar day begins locally. */
function startOfLocalDay(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  const wall = Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
  const firstOffset = zoneOffsetMilliseconds(wall);
  const utc = wall - firstOffset;
  const secondOffset = zoneOffsetMilliseconds(utc);
  return secondOffset === firstOffset ? utc : wall - secondOffset;
}

/** A calendar date in the application zone, as an `<input type="date">` value. */
function localDateOf(instant: number): string {
  const { year, month, day } = zonedParts(instant);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Calendar arithmetic on a `YYYY-MM-DD` value, with no zone involved. */
function addCalendarDays(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00.000Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/**
 * Today's date where the member is, not where the server keeps its clock.
 *
 * Bounds the date picker. Reading it from a UTC instant put the ceiling a day
 * behind the member's own calendar between local midnight and 03:00, so between
 * those hours the picker refused a date the wall behind them already showed.
 */
export function todayDateInput(now: Date = new Date()): string {
  return localDateOf(now.getTime());
}

/**
 * The instant a picked calendar day begins, locally.
 *
 * This is the boundary's meaning and it has to agree with
 * {@link formatHistoryRange}, which prints the same instant back as a date. A
 * value that is not a plain calendar date is already an instant and passes
 * through: the control plane sends back what it stored.
 */
export function dateInputToIso(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return new Date(startOfLocalDay(value)).toISOString();
}

/** The boundary N calendar days back from today, at local midnight. */
export function historyDateForDays(
  days: number,
  now: Date = new Date(),
): string {
  return dateInputToIso(addCalendarDays(todayDateInput(now), -days));
}

/**
 * Whether a stored boundary is one this surface would have offered. The ceiling
 * is the end of today locally, so a member picking today at 01:00 their own time
 * is not refused for a date the UTC clock has not reached yet.
 */
export function isAllowedHistoryDate(
  value: string,
  now: Date = new Date(),
): boolean {
  const instant = Date.parse(value);
  if (!Number.isFinite(instant)) return false;
  const earliest = startOfLocalDay(EARLIEST_HISTORY_DATE);
  const endOfToday = startOfLocalDay(addCalendarDays(todayDateInput(now), 1));
  return instant >= earliest && instant < endOfToday;
}

export interface SyncTiming {
  readonly elapsed: string | null;
  readonly remaining: string | null;
}

/**
 * Formats the persisted boundary without converting it to a preset.
 *
 * Printed in the application's zone, which is the zone the instant was built
 * from by {@link dateInputToIso}. That pairing is the whole point: a boundary
 * printed in one zone and meant in another is a label and a meaning a day
 * apart, and which day it lands on is exactly what a member is choosing.
 *
 * The zone is still named on screen. Every other date here renders in the
 * reader's own, so without the label a member reads two clocks in one interface
 * and cannot tell which is which. It stays inside the same line rather than
 * taking a note of its own — a whole row to say one word is exactly the density
 * this product refuses.
 */
export function formatHistoryRange(
  historyStartAt: string | null,
  locale: string,
): string {
  if (historyStartAt === null) return "Tüm geçmiş";

  const date = new Date(historyStartAt);
  if (Number.isNaN(date.getTime())) {
    throw new RangeError("The history start date is invalid.");
  }

  const shown = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: APP_TIME_ZONE,
    year: "numeric",
  }).format(date);
  return `${shown} ${APP_TIME_ZONE_LABEL} tarihinden beri`;
}

/**
 * Returns elapsed time and a pace-based ETA only when the denominator is exact.
 * An unavailable or upper-bound total must be passed as null.
 */
export function formatSyncTiming(
  startedAt: string | null,
  processed: number,
  exactTotal: number | null,
  now: Date,
): SyncTiming {
  const elapsedMilliseconds = syncElapsedMilliseconds(startedAt, now);
  if (elapsedMilliseconds === null) {
    return { elapsed: null, remaining: null };
  }

  const elapsed = `${formatCompactDuration(elapsedMilliseconds)} geçti`;
  if (
    exactTotal === null ||
    !Number.isSafeInteger(processed) ||
    !Number.isSafeInteger(exactTotal) ||
    elapsedMilliseconds === 0 ||
    processed <= 0 ||
    exactTotal <= processed
  ) {
    return { elapsed, remaining: null };
  }

  const remainingMilliseconds =
    (elapsedMilliseconds * (exactTotal - processed)) / processed;
  return {
    elapsed,
    remaining:
      remainingMilliseconds < minuteMilliseconds
        ? "Bir dakikadan az kaldı"
        : `Yaklaşık ${formatCompactDuration(remainingMilliseconds)} kaldı`,
  };
}

function syncElapsedMilliseconds(
  startedAt: string | null,
  now: Date,
): number | null {
  if (startedAt === null || Number.isNaN(now.getTime())) return null;
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return null;
  const elapsed = now.getTime() - started;
  return elapsed >= 0 ? elapsed : null;
}

function formatCompactDuration(milliseconds: number): string {
  if (milliseconds < minuteMilliseconds) {
    return `${Math.max(0, Math.floor(milliseconds / 1_000))} sn`;
  }

  const roundedMinutes = Math.max(
    1,
    Math.round(milliseconds / minuteMilliseconds),
  );
  if (roundedMinutes < 60) return `${roundedMinutes} dk`;

  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  return minutes === 0 ? `${hours} sa` : `${hours} sa ${minutes} dk`;
}
